import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { cases as casesApi, errorMessage } from '../utils/api';
import { useSocket } from '../context/SocketContext';

/** باید با StandardPagination.page_size در بک‌اند یکی باشد. */
export const PAGE_SIZE = 50;

/**
 * فهرست پرونده‌ها با تازه‌سازی خودکار وقتی سرور خبر تغییر می‌دهد.
 * اگر WebSocket وصل نباشد، هر ۲۰ ثانیه یک‌بار خودش سر می‌زند.
 *
 * سرور صفحه‌بندی می‌کند؛ اینجا شماره‌ی صفحه نگه داشته می‌شود تا فهرست‌های
 * بلند (بایگانی، تحویل‌شده‌ها) در پرونده‌ی پنجاهم بی‌صدا قطع نشوند.
 */
export function useCases(params = {}, { pageSize = PAGE_SIZE } = {}) {
  const { caseVersion, connected } = useSocket();
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const serialized = JSON.stringify(params);
  const firstLoad = useRef(true);

  // تغییر فیلتر یا تب یعنی فهرست دیگری؛ از صفحه‌ی اول شروع کن.
  useEffect(() => {
    setPage(1);
  }, [serialized]);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await casesApi.list({
        ...JSON.parse(serialized),
        page,
        page_size: pageSize,
      });
      const results = payload.results ?? [];
      const total = payload.count ?? 0;

      // پرونده‌ها با هر اقدام از صف بیرون می‌روند. اگر آخرین پرونده‌ی صفحه‌ی
      // آخر رفته باشد، صفحه‌ی خالی می‌ماند — یک پله عقب برو.
      if (!results.length && total > 0 && page > 1) {
        setPage((p) => Math.min(p - 1, Math.max(1, Math.ceil(total / pageSize))));
        return;
      }

      setData(results);
      setCount(total);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'فهرست پرونده‌ها بارگذاری نشد.'));
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, [serialized, page, pageSize]);

  useEffect(() => {
    setLoading(firstLoad.current);
    load();
  }, [load, caseVersion]);

  useEffect(() => {
    if (connected) return undefined;
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [connected, load]);

  return {
    cases: data,
    count,
    page,
    setPage,
    pageSize,
    pageCount: Math.max(1, Math.ceil(count / pageSize)),
    loading,
    error,
    reload: load,
  };
}

/** اجرای یک اقدام روی پرونده با مدیریت خطا و وضعیت «در حال ارسال». */
export function useCaseAction(onDone) {
  const [pendingId, setPendingId] = useState(null);

  const run = useCallback(
    async (id, action, successMessage) => {
      setPendingId(id);
      try {
        const { data } = await action();
        if (successMessage) toast.success(successMessage);
        onDone?.(data);
        return data;
      } catch (err) {
        toast.error(errorMessage(err));
        onDone?.(null);
        return null;
      } finally {
        setPendingId(null);
      }
    },
    [onDone]
  );

  return { run, pendingId };
}
